/**
 * @architecture-header 1
 * @layer shared
 * @domain workspace-shell
 * @responsibility Render the context menu rows and the row-menu helper.
 * @state-owner overlay host
 * @allowed-imports React; overlay host
 * @forbidden business rules; network
 * @public-api workspace modules
 * @side-effects opens popovers through the overlay host
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type { ReactNode } from 'react';
import { useOverlay } from './OverlayHost';

export interface MenuItem {
  readonly label: ReactNode;
  readonly onSelect: () => void;
  readonly shortcut?: string;
  readonly danger?: boolean;
  readonly disabled?: boolean;
}

export type MenuEntry = MenuItem | 'divider';

/** Dividers have no identity of their own, so they borrow the label that follows them. */
function dividerKey(items: readonly MenuEntry[], index: number): string {
  const next = items.slice(index + 1).find((entry): entry is MenuItem => entry !== 'divider');
  return `divider-before-${next ? String(next.label) : 'end'}`;
}

export function MenuRows({ items }: { items: readonly MenuEntry[] }) {
  const { closePopover } = useOverlay();
  return (
    <>
      {items.map((item, index) =>
        item === 'divider' ? (
          <div className="mdiv" key={dividerKey(items, index)} />
        ) : (
          <button
            className={`mit2${item.danger ? ' danger' : ''}`}
            disabled={item.disabled}
            key={String(item.label)}
            type="button"
            onClick={() => {
              closePopover();
              item.onSelect();
            }}
          >
            {item.label}
            {item.shortcut ? (
              <span className="cmdk">
                {item.shortcut.split(' ').map((key) => (
                  <kbd key={key}>{key}</kbd>
                ))}
              </span>
            ) : null}
          </button>
        ),
      )}
    </>
  );
}

export interface CommandMenuItem {
  readonly icon: ReactNode;
  readonly title: string;
  readonly description: ReactNode;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  readonly tone?: 'primary' | 'muted';
}

/** The rich menu rows. */
export function CommandMenuRows({
  heading,
  items,
  empty,
}: {
  heading: ReactNode;
  items: readonly CommandMenuItem[];
  empty?: ReactNode;
}) {
  const { closePopover } = useOverlay();
  return (
    <>
      <div className="chd3">{heading}</div>
      {items.length
        ? items.map((item) => (
            <button
              className="ci2"
              disabled={item.disabled}
              key={item.title}
              type="button"
              onClick={() => {
                closePopover();
                item.onSelect();
              }}
            >
              <span
                className="sqc"
                style={
                  item.tone === 'primary'
                    ? { background: 'var(--primary-pale)', color: 'var(--primary)' }
                    : { background: 'var(--sc)', color: 'var(--outline)' }
                }
              >
                {item.icon}
              </span>
              <span>
                <b>{item.title}</b>
                <span>{item.description}</span>
              </span>
            </button>
          ))
        : (empty ?? null)}
    </>
  );
}
